/**
 * CaptureButton — Bouton de capture caméra
 *
 * SVG inline pour le rendu parfait à toutes densités.
 * Framer Motion pour la pulsation au repos et le feedback au tap.
 *
 * Props :
 *   onCapture  {function}  — déclenché au tap
 *   isLoading  {boolean}   — affiche spinner + désactive le bouton
 *   disabled   {boolean}   — désactive sans spinner
 *
 * Anatomie :
 *   Anneau extérieur 76×76px — blanc cassé 3px, fond noir semi-opaque
 *   Disque intérieur 56×56px — blanc cassé plein
 *   Icône caméra SVG 28px   — noir nuit (repos) | spinner cuivre (loading)
 */

import React, { useState } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

const COPPER    = '#B08D5B'
const NIGHT     = '#1A1D24'
const OFF_WHITE = '#F8F4EC'

// ── Icône caméra ────────────────────────────────────────────────────────────
const CameraIcon = ({ color = NIGHT }) => (
  <svg
    width={28} height={28}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
)

// ── Spinner cuivre ──────────────────────────────────────────────────────────
const SpinnerIcon = () => (
  <motion.svg
    width={26} height={26}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    animate={{ rotate: 360 }}
    transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
  >
    <circle cx="12" cy="12" r="10" stroke={OFF_WHITE} strokeWidth={2} strokeOpacity={0.2}/>
    <path
      d="M12 2 a10 10 0 0 1 8.66 5"
      stroke={COPPER}
      strokeWidth={2.5}
      strokeLinecap="round"
    />
  </motion.svg>
)

// ── Composant principal ─────────────────────────────────────────────────────
const CaptureButton = ({ onCapture, isLoading = false, disabled = false }) => {
  const [pressed, setPressed] = useState(false)
  const prefersReduced = useReducedMotion()

  const handleClick = () => {
    if (isLoading || disabled) return
    setPressed(true)
    setTimeout(() => setPressed(false), 150)
    onCapture?.()
  }

  return (
    <motion.button
      onClick={handleClick}
      disabled={isLoading || disabled}
      aria-label={isLoading ? 'Identification en cours' : 'Capturer l\'œuvre'}
      aria-busy={isLoading}
      // Pulsation au repos
      animate={
        !isLoading && !pressed && !prefersReduced
          ? { scale: [1, 1.05, 1] }
          : { scale: pressed ? 0.92 : 1 }
      }
      transition={
        !isLoading && !pressed && !prefersReduced
          ? { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }
          : { duration: 0.15, ease: [0.34, 1.56, 0.64, 1] }
      }
      style={{
        background:  'transparent',
        border:      'none',
        padding:     0,
        cursor:      isLoading || disabled ? 'not-allowed' : 'pointer',
        opacity:     disabled ? 0.5 : 1,
        display:     'flex',
        alignItems:  'center',
        justifyContent: 'center',
        // Zone tactile WCAG — 76px dépasse déjà le minimum de 44px
        width:  '76px',
        height: '76px',
        position: 'relative',
      }}
    >
      <svg
        width={76} height={76}
        viewBox="0 0 76 76"
        xmlns="http://www.w3.org/2000/svg"
        overflow="visible"
        aria-hidden="true"
      >
        {/* Anneau extérieur */}
        <circle
          cx={38} cy={38} r={36}
          fill="rgba(26,29,36,0.40)"
          stroke={OFF_WHITE}
          strokeWidth={3}
        />
        {/* Disque intérieur */}
        <circle
          cx={38} cy={38} r={27}
          fill={OFF_WHITE}
        />
      </svg>

      {/* Icône centrée par-dessus le SVG */}
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <AnimatePresence mode="wait">
          {isLoading
            ? <SpinnerIcon key="spinner" />
            : (
              <motion.div
                key="camera"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
              >
                <CameraIcon />
              </motion.div>
            )
          }
        </AnimatePresence>
      </div>
    </motion.button>
  )
}

export default CaptureButton
